
import {
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import { colors, semantic } from "../../theme/tokens";
import type { WidgetProps } from "../types";
import { formatCurrency2, formatGeneratedAt } from "./format";

// Live-data widget #4: where Bison's pricing sits vs. the market on each ASIN.
export function PricingCompetitiveness({ widget }: WidgetProps<"pricing_competitiveness">) {
    const { props, data } = widget;
    const title = props.title ?? "Pricing position";

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Snapshot · {formatGeneratedAt(data.generatedAt)}
            </Typography>

            <TableContainer
                sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "10px",
                    bgcolor: "background.paper",
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: "primary.main" }}>
                            <TableCell sx={CELL_HEAD}>ASIN</TableCell>
                            <TableCell sx={CELL_HEAD}>Title</TableCell>
                            <TableCell sx={CELL_HEAD} align="right">
                                Bison price
                            </TableCell>
                            <TableCell sx={CELL_HEAD} align="right">
                                Lowest competitor
                            </TableCell>
                            <TableCell sx={CELL_HEAD} align="center">
                                Position
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.items.map((row) => {
                            const meta =
                                row.pricePosition != null
                                    ? POSITION_META[row.pricePosition]
                                    : { label: "Not priced", bg: colors.lightGray, color: colors.darkGray };
                            return (
                                <TableRow key={row.asin} hover>
                                    <TableCell
                                        sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                                    >
                                        {row.asin}
                                    </TableCell>
                                    <TableCell>{row.title}</TableCell>
                                    <TableCell align="right">
                                        {formatCurrency2(row.bisonPrice)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatCurrency2(row.lowestCompetitorPrice)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            size="small"
                                            label={meta.label}
                                            sx={{
                                                bgcolor: meta.bg,
                                                color: meta.color,
                                                fontWeight: 600,
                                                fontSize: "0.7rem",
                                                textTransform: "uppercase",
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

const CELL_HEAD = {
    color: "common.white",
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: 0.5,
};

const POSITION_META = {
    lowest: { label: "Lowest", bg: semantic.info.bg, color: semantic.info.text },
    competitive: { label: "Competitive", bg: colors.lightGray, color: colors.darkGray },
    above_market: { label: "Above market", bg: semantic.warning.bg, color: semantic.warning.text },
} as const;
